import {
  Button,
  Flex,
  FormLabel,
  IconButton,
  Text,
  VStack,
} from "@chakra-ui/react";
import { FieldArray, useField } from "formik";
import { InputControl, SelectControl } from "formik-chakra-ui";
import { RiAddLine, RiDeleteBin6Line } from "react-icons/ri";
import { Card } from "src/components/Layout/Card";
import { type EligibilityDataPoint } from "src/generated/hooks/form-service/requests";
import * as RD from "src/types/remoteData";
import { emptyAnswerOption, type AnswerOption } from "./answerSelection";
import { DataPointSelect } from "./DataPointSelect";

export function AnswerSelectionConditionCard({
  name,
  enrollmentPeriodId,
  dataPoints,
  onDelete,
}: {
  name: string;
  enrollmentPeriodId: string;
  dataPoints: RD.RemoteData<Error, EligibilityDataPoint[]>;
  onDelete: () => void;
}) {
  const [answerOptionsField] = useField<AnswerOption[]>(
    `${name}.answerOptions`
  );
  const answerOptions = answerOptionsField.value;

  return (
    <Card showBorder>
      <VStack align="stretch" spacing={4}>
        <Flex align="center" justify="space-between" gap={3}>
          <Text fontSize="md" fontWeight="medium" color="blackAlpha.800">
            Answer selection
          </Text>
          <IconButton
            aria-label="Delete answer selection"
            icon={<RiDeleteBin6Line />}
            variant="outline"
            colorScheme="gray"
            size="sm"
            onClick={onDelete}
          />
        </Flex>

        <DataPointSelect
          name={`${name}.dataPointId`}
          enrollmentPeriodId={enrollmentPeriodId}
          dataPoints={dataPoints}
          dataPointType="SingleSelect"
        />

        <SelectControl
          name={`${name}.operator`}
          label="Is"
          selectProps={{ placeholder: "Select" }}
        >
          <option value="in">Any of</option>
          <option value="notIn">Not any of</option>
        </SelectControl>

        <FieldArray name={`${name}.answerOptions`}>
          {(helper) => (
            <VStack align="stretch" spacing={2}>
              <FormLabel>Option value(s)</FormLabel>
              {answerOptions.map((answerOption, index) => (
                <Flex key={answerOption.id} align="flex-start" gap={2}>
                  <InputControl
                    name={`${name}.answerOptions.${index}.value`}
                    flexGrow={1}
                  />
                  {answerOptions.length > 1 ? (
                    <Flex direction="column" flexShrink={0}>
                      <Flex align="center" h="2.5rem">
                        <IconButton
                          type="button"
                          aria-label="Remove option value"
                          icon={<RiDeleteBin6Line />}
                          variant="outline"
                          colorScheme="gray"
                          size="sm"
                          onClick={() => helper.remove(index)}
                        />
                      </Flex>
                    </Flex>
                  ) : null}
                </Flex>
              ))}
              <Button
                type="button"
                variant="outline"
                colorScheme="gray"
                size="sm"
                alignSelf="flex-start"
                leftIcon={<RiAddLine />}
                onClick={() => helper.push(emptyAnswerOption())}
              >
                Add option value
              </Button>
            </VStack>
          )}
        </FieldArray>
      </VStack>
    </Card>
  );
}
